import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { getCookie, setCookie } from "utils/v3/CookieUtil";
import { Box, Grid, Typography } from "@mui/material";
import HeaderLogo from "../../../../assets/images/LogoIconDefault.svg";
import LoginCallbackPageStyles from "./LoginCallbackPage.styles";

import {
  setMembershipRenewalPayBlocked,
  setUserPersonalInformation,
  updateFieldsInUserPersonalInformation
} from "../../../../store/reducers/user/UserAccountSlice";
import {
  identifyMember,
  trackAuthCompleted,
  trackAuthFailed
} from "analytics";
import { PATHS } from "../../../../constants/pathConstants";
import Translate from "../../../../translate/Translate";
import GLTextField from "components/v3/forms/formComponents/GLTextField";
import { useFormFieldState } from "../../Forms/FormUtils";
import { testAccessCode } from "../../../../apis/authApi";
import { useWindowSize } from "../../../../hooks/useWindowSize";
import GLButton from "components/v3/sharedComponents/GLButton";
import HelpModal from "components/v3/pages/onboarding/RegistrationInstructions/HelpModal";
import LoadingPage from "components/v3/pages/LoadingPage";
import {
  findValueByKey,
  getIsMemberActiveFromIdTokenClaims,
  getShareEligibilityFromIdTokenClaims
} from "utils/v3/auth0ClaimsUtils";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";

const LoginCallbackPage = () => {
  const { isAuthenticated, getAccessTokenSilently, getIdTokenClaims } =
    useAuth0();
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = LoginCallbackPageStyles();
  const windowSize = useWindowSize();
  const { formFields, handleInputChange } = useFormFieldState({});
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [needsAccessToken, setNeedsAccessToken] = useState(false);
  const [lastCodeWasInvalid, setLastCodeWasInvalid] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);

  //This feels gross, but there are a few reasons we need the useEffect loop to
  //  kick over again during the back and forth redirects required by the auth0 flow
  //  this lets us tell the effect to run again on the next page load.
  const [refreshLoopCount, setRefreshLoopCount] = useState(0);

  const [userInfo, setUserInfo] = useState({});

  useEffect(() => {
    let isMounted = true; // Flag to track component mount state

    const getToken = async () => {
      try {
        const token = await getAccessTokenSilently();

        if (!isMounted) return; // Exit early if the component is unmounted

        const userDataFromAuth0Token = await getIdTokenClaims();

        if (!isMounted) return; // Exit early if the component is unmounted

        const userGlid = findValueByKey(userDataFromAuth0Token, "glMemberId");
        const glProgramId = findValueByKey(
          userDataFromAuth0Token,
          "glProgramId"
        );
        const auth0UserName = findValueByKey(userDataFromAuth0Token, "email");
        const auth0UserId = findValueByKey(userDataFromAuth0Token, "sub");

        dispatch(
          updateFieldsInUserPersonalInformation({
            glMemberId: userGlid,
            email: userDataFromAuth0Token.email,
            glProgramId: glProgramId,
            isMemberActive: getIsMemberActiveFromIdTokenClaims(
              userDataFromAuth0Token
            ),
            ...getShareEligibilityFromIdTokenClaims(userDataFromAuth0Token)
          })
        );

        dispatch(setMembershipRenewalPayBlocked(false));

        if (userGlid) {
          identifyMember(userGlid);
        }

        setUserInfo({
          glid: userGlid,
          auth0UserName: auth0UserName,
          auth0UserId: auth0UserId
        });

        setCookie("accessToken", token);
        setRefreshLoopCount((prev) => prev + 1);

        if (userGlid) {
          history.push(PATHS.API_SCRAPE);
        } else {
          setNeedsAccessToken(true);
        }
      } catch (error) {
        if (
          [
            "invalid_grant",
            "missing_refresh_token",
            "consent_required"
          ].includes(error.error)
        ) {
          // Ignore specific errors
          setRefreshLoopCount((prev) => prev + 1);
        } else if (error.error === "Unknown or invalid refresh token") {
          history.push(PATHS.LOG_OUT);
        } else {
          trackAuthFailed("access_token", error);
          throw error;
        }
      }
    };

    if (!getCookie("accessToken")) {
      getToken();
    }

    return () => {
      isMounted = false; // Cleanup: prevent state updates if unmounted
    };
  }, [isAuthenticated, refreshLoopCount]); // Dependencies

  const sendAccessCodeTest = () => {
    setShowLoadingSpinner(true);
    testAccessCode(
      formFields.accessCode,
      userInfo.auth0UserName,
      userInfo.auth0UserId
    ).then((result) => {
      // doApiCall resolves with AxiosError on HTTP/network failure (see apis/v3/utils doApiCall).
      if (axios.isAxiosError(result)) {
        setShowLoadingSpinner(false);
        const status = result.response?.status;
        if (status === 401 || status === 403) {
          trackAuthFailed("access_code_lookup", result, {
            failureReason: "unauthorized",
            httpStatus: status
          });
          history.push(PATHS.LOG_OUT);
          return;
        }
        trackAuthFailed("access_code_lookup", result, {
          failureReason: "invalid_code",
          httpStatus: status
        });
        setLastCodeWasInvalid(true);
        return;
      }

      const response = result;
      if (response.status >= 200 && response.status < 300) {
        const userInfoFromResponse = {
          firstName: response.data.firstName,
          lastName: response.data.lastName
        };
        setUserInfo({ ...userInfo, ...userInfoFromResponse });
        dispatch(
          setUserPersonalInformation({
            ...userInfoFromResponse,
            email: userInfo.auth0UserName
          })
        );
        if (response.data.glMemberId) {
          identifyMember(response.data.glMemberId);
          trackAuthCompleted();
        }
        history.push(PATHS.API_SCRAPE);
      } else {
        trackAuthFailed("access_code_lookup", undefined, {
          failureReason: "invalid_code",
          httpStatus: response.status
        });
        setShowLoadingSpinner(false);
        setLastCodeWasInvalid(true);
      }
    });
  };

  const resetInvalidCodeFlagAndUpdateInput = (input) => {
    setLastCodeWasInvalid(false);
    handleInputChange(input);
  };

  const accessCodeInvalidMessage = () => {
    if (lastCodeWasInvalid) {
      return "We're having trouble finding your code, please check and try again.";
    }
    return undefined;
  };

  const renderAccessCodeForm = () => {
    return (
      <GradientFramedLayout showFooter={false}>
        <HelpModal
          isOpen={contactModalOpen}
          onClose={() => {
            setContactModalOpen(false);
          }}
        />
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          direction="column"
          className={classes.pageContainer}
          style={{
            width: "95%",
            height: "100%",
            position: "relative",
            minHeight: windowSize.height
          }}
        >
          <Grid item xs={11} sm={8} md={5} className={classes.formContainer}>
            <Grid item className={classes.center}>
              <img src={HeaderLogo} className={classes.headerLogo} />
            </Grid>
            <Grid item className={classes.center}>
              <Typography variant="deprecated_h5">
                <Translate text="Enter your code" />
              </Typography>
              <Typography
                variant="deprecated_subtitle2"
                sx={{ maxWidth: "500px", display: "inline-block" }}
              >
                <Translate text="Your code should be in your email. Look for an email from noreply@genomiclife-mail.com - this code gives you access to the portal and connects your account to your plan." />
              </Typography>
            </Grid>
            <Grid item className={`${classes.center} ${classes.input}`}>
              <GLTextField
                id="accessCode"
                label={"Access Code"}
                value={formFields.accessCode ?? ""}
                onChange={resetInvalidCodeFlagAndUpdateInput}
                additionalValidation={accessCodeInvalidMessage}
                required
              />
            </Grid>
            <Grid
              item
              className={classes.center}
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <Grid item>
                <GLButton
                  loading={showLoadingSpinner}
                  onClick={sendAccessCodeTest}
                  disabled={!formFields.accessCode}
                  text={"Continue"}
                  sx={{
                    marginBottom: "25px"
                  }}
                ></GLButton>
              </Grid>
            </Grid>
          </Grid>
          <Typography variant="body_secondary" sx={{ mt: "40px" }}>
            <Translate text="Having trouble? " />
            <Box
              className={classes.link}
              onClick={() => setContactModalOpen(true)}
            >
              <strong>
                <Translate text="Please let us know" />
              </strong>
            </Box>
          </Typography>
        </Grid>
      </GradientFramedLayout>
    );
  };

  if (needsAccessToken) {
    return renderAccessCodeForm();
  } else {
    return <LoadingPage message={"Retrieving your account..."} />;
  }
};

export default LoginCallbackPage;
